import Conditions from '../../../../../resources/conditions';
import { Responses } from '../../../../../resources/responses';
import ZoneId from '../../../../../resources/zone_id';
import { RaidbossData } from '../../../../../types/data';
import { TriggerSet } from '../../../../../types/trigger';

export type Data = RaidbossData;

const triggerSet: TriggerSet<Data> = {
  id: 'YuweyawataFieldStation',
  zoneId: ZoneId.YuweyawataFieldStation,
  timelineFile: 'yuweyawata.txt',
  triggers: [
    // ---------------- Lindblum Zaghnal ---------------- //
    {
      id: 'Yuweyawata Lindblum Zaghnal Electrical Overload',
      type: 'StartsUsing',
      netRegex: { id: '9EBB', source: 'Lindblum Zaghnal', capture: false },
      response: Responses.aoe(),
    },
    {
      id: 'Yuweyawata Lindblum Zaghnal Caber Toss',
      type: 'StartsUsing',
      netRegex: { id: '9EB0', source: 'Lindblum Zaghnal', capture: true },
      delaySeconds: (_data, matches) => parseFloat(matches.castTime) - 5,
      durationSeconds: 5,
      infoText: (_data, _matches, output) => {
        return output.text!();
      },
      outputStrings: {
        text: {
          en: 'Away from falling pillar',
          de: 'Weg von der fallenden Säule',
          fr: 'Éloignez-vous du pilier',
          cn: '远离柱子落点',
          ko: '기둥 추락지점에서 멀리 떨어지기',
          tc: '遠離柱子落點',
        },
      },
    },
    {
      id: 'Yuweyawata Lindblum Zaghnal Lightning Storm',
      type: 'StartsUsing',
      netRegex: { id: '9EBD', source: 'Lindblum Zaghnal', capture: true },
      condition: Conditions.targetIsYou(),
      response: Responses.spread(),
    },
    {
      id: 'Yuweyawata Lindblum Zaghnal Sparking Fissure (small)',
      type: 'StartsUsing',
      netRegex: { id: ['9EB7', 'A133'], source: 'Lindblum Zaghnal', capture: false },
      response: Responses.aoe(),
    },
    {
      id: 'Yuweyawata Lindblum Zaghnal Raw Electrope Spawn',
      type: 'AddedCombatant',
      // 13622 = Raw Electrope
      netRegex: { npcNameId: '13622', capture: false },
      suppressSeconds: 1,
      response: Responses.killAdds(),
    },
    {
      id: 'Yuweyawata Lindblum Zaghnal Sparking Fissure (big)',
      type: 'StartsUsing',
      netRegex: { id: 'A12A', source: 'Lindblum Zaghnal', capture: true },
      delaySeconds: (_data, matches) => parseFloat(matches.castTime) - 5,
      durationSeconds: 5,
      response: Responses.bigAoe('alert'),
    },

    // ---------------- Overseer Kanilokka ---------------- //
    {
      id: 'Yuweyawata Overseer Kanilokka Dark Souls',
      type: 'StartsUsing',
      netRegex: { id: '9ED2', source: 'Overseer Kanilokka', capture: true },
      response: Responses.tankBuster(),
    },
    {
      id: 'Yuweyawata Overseer Kanilokka Free Spirits',
      type: 'StartsUsing',
      netRegex: { id: '9EC0', source: 'Overseer Kanilokka', capture: false },
      response: Responses.aoe(),
    },
    {
      id: 'Yuweyawata Overseer Kanilokka Phantom Flood',
      type: 'StartsUsing',
      netRegex: { id: '9EC4', source: 'Overseer Kanilokka', capture: false },
      response: Responses.getIn(),
    },
    {
      id: 'Yuweyawata Overseer Kanilokka Telltale Tears',
      type: 'StartsUsing',
      netRegex: { id: '9EC9', source: 'Overseer Kanilokka', capture: true },
      condition: Conditions.targetIsYou(),
      response: Responses.spread(),
    },
    {
      id: 'Yuweyawata Overseer Kanilokka Lost Hope',
      type: 'Ability',
      netRegex: { id: '9EC5', source: 'Overseer Kanilokka', capture: false },
      durationSeconds: 17,
      suppressSeconds: 1,
      response: Responses.getOut(),
    },
    {
      id: 'Yuweyawata Overseer Kanilokka Necrohazard',
      type: 'StartsUsing',
      netRegex: { id: '9EC6', source: 'Overseer Kanilokka', capture: true },
      delaySeconds: (_data, matches) => parseFloat(matches.castTime) - 4,
      response: Responses.bigAoe(),
    },
    {
      id: 'Yuweyawata Overseer Kanilokka Bloodburst',
      type: 'StartsUsing',
      netRegex: { id: '9EC7', source: 'Overseer Kanilokka', capture: false },
      response: Responses.aoe(),
    },
    {
      id: 'Yuweyawata Overseer Kanilokka Soul Douse',
      type: 'StartsUsing',
      netRegex: { id: '9ECB', source: 'Overseer Kanilokka', capture: true },
      response: Responses.stackMarkerOn(),
    },

    // ---------------- Lunipyati ---------------- //
    {
      id: 'Yuweyawata Lunipyati Raging Claw',
      type: 'StartsUsing',
      netRegex: { id: '9EA4', source: 'Lunipyati', capture: false },
      response: Responses.getBehind(),
    },
    {
      id: 'Yuweyawata Lunipyati Leporine Loaf',
      type: 'StartsUsing',
      netRegex: { id: '9E9B', source: 'Lunipyati', capture: false },
      response: Responses.aoe(),
    },
    {
      id: 'Yuweyawata Lunipyati Jagged Edge',
      type: 'StartsUsing',
      netRegex: { id: '9EA7', source: 'Lunipyati', capture: true },
      condition: Conditions.targetIsYou(),
      response: Responses.spread(),
    },
    {
      id: 'Yuweyawata Lunipyati Crater Carve',
      type: 'StartsUsing',
      netRegex: { id: '9E9D', source: 'Lunipyati', capture: true },
      delaySeconds: (_data, matches) => parseFloat(matches.castTime) - 4,
      response: Responses.getOut(),
    },
    {
      id: 'Yuweyawata Lunipyati Beastly Roar',
      type: 'StartsUsing',
      netRegex: { id: '9EA2', source: 'Lunipyati', capture: false },
      alertText: (_data, _matches, output) => {
        return output.text!();
      },
      outputStrings: {
        text: {
          en: 'Away from boss',
          de: 'Weg vom Boss',
          fr: 'Éloignez-vous du boss',
          cn: '远离 BOSS',
          ko: '보스에게서 멀어지기',
          tc: '遠離 BOSS',
        },
      },
    },
    {
      id: 'Yuweyawata Lunipyati Turali Stone IV',
      type: 'StartsUsing',
      netRegex: { id: '9EA8', source: 'Lunipyati', capture: true },
      response: Responses.stackMarkerOn(),
    },
    {
      id: 'Yuweyawata Lunipyati Sonic Howl',
      type: 'StartsUsing',
      netRegex: { id: '9EAA', source: 'Lunipyati', capture: false },
      response: Responses.aoe(),
    },
    {
      id: 'Yuweyawata Lunipyati Slabber',
      type: 'StartsUsing',
      netRegex: { id: '9EAB', source: 'Lunipyati', capture: true },
      response: Responses.tankBuster(),
    },
  ],
  timelineReplace: [
    {
      'locale': 'de',
      'replaceSync': {
        'Lindblum Zaghnal': 'Lindblum-Zaghnal',
        'Lunipyati': 'Lunipyati',
        'Overseer Kanilokka': 'Forschungsleiter Kanilokka',
        'Preserved Soul': 'elendig(?:e|er|es|en) Seele',
      },
      'replaceText': {
        '\\(arcs\\)': '(Bögen)',
        '\\(big\\)': '(groß)',
        '\\(multiple\\)': '(mehrere)',
        '\\(spiral\\)': '(Spirale)',
        '--adds 1 targetable--': '--Adds 1 anvisierbar--',
        '--adds 2 targetable--': '--Adds 2 anvisierbar--',
        '--adds 3 targetable--': '--Adds 3 anvisierbar--',
        'Beastly Roar': 'Bestialisches Brüllen',
        'Bloodburst': 'Blutstoß',
        'Boulder Dance': 'Tanzende Läufer',
        'Caber Toss': 'Pfahlwurf',
        'Cell Shock': 'Entladung',
        'Crater Carve': 'Kraterschnitzer',
        'Dark II': 'Negra',
        'Dark Souls': 'Dunkelste Seelenernte',
        'Electrical Overload': 'Überentladung',
        'Free Spirits': 'Ungebändigte Seelen',
        'Gore': 'Ladehauer',
        'Jagged Edge': 'Schartenkante',
        'Leaping Earth': 'Hoppelnde Erde',
        'Leporine Loaf': 'Hasenklein',
        'Lightning Bolt': 'Blitzschlag',
        'Lightning Storm': 'Blitzsturm',
        'Line Voltage': 'Linearentladung',
        'Lost Hope': 'Klage der Ohnmächtigen',
        'Necrohazard': 'Nekrotische Gefahr',
        'Phantom Flood': 'Phantomflut',
        'Raging Claw': 'Wütende Kralle',
        'Rock Blast': 'Brockenstoß',
        'Slabber': 'Schlabber',
        'Sonic Howl': 'Schallheuler',
        'Soul Douse': 'Seelenertränker',
        'Soulweave': 'Seelengeflecht',
        'Sparking Fissure': 'Britzelspalt',
        'Telltale Tears': 'Beseelte Tränen',
        'Turali Stone IV': 'Brockenspitze',
      },
    },
    {
      'locale': 'fr',
      'replaceSync': {
        'Lindblum Zaghnal': 'zaghnal de Lindblum',
        'Lunipyati': 'Lunipyati',
        'Overseer Kanilokka': 'Kanilokka',
        'Preserved Soul': 'âme préservée',
      },
      'replaceText': {
        '\\(arcs\\)': '(Arcs)',
        '\\(big\\)': '(Gros)',
        '\\(multiple\\)': '(Multiple)',
        '\\(spiral\\)': '(Spirale)',
        '--adds 1 targetable--': '--Adds 1 ciblable--',
        '--adds 2 targetable--': '--Adds 2 ciblable--',
        '--adds 3 targetable--': '--Adds 3 ciblable--',
        'Beastly Roar': 'Rugissement bestial',
        'Bloodburst': 'Explosion sanguine',
        'Boulder Dance': 'Danse du roc',
        'Caber Toss': 'Lancer d\'électrope',
        'Cell Shock': 'Surtension',
        'Crater Carve': 'Ciselage terrestre',
        'Dark II': 'Extra Ténèbres',
        'Dark Souls': 'Âme ténébreuse',
        'Electrical Overload': 'Électrification étendue',
        'Free Spirits': 'Libération des âmes',
        'Gore': 'Croc perçant',
        'Jagged Edge': 'Pointes acérées',
        'Leaping Earth': 'Terre bondissante',
        'Leporine Loaf': 'Lapin sphinx',
        'Lightning Bolt': 'Éclair de foudre',
        'Lightning Storm': 'Pluie d\'éclairs',
        'Line Voltage': 'Électrification linéaire',
        'Lost Hope': 'Cri hébété',
        'Necrohazard': 'Danger nécrotique',
        'Phantom Flood': 'Déluge spectral',
        'Raging Claw': 'Griffes enragées',
        'Rock Blast': 'Rafale rocailleuse',
        'Slabber': 'Glaire fétide',
        'Sonic Howl': 'Hurlement sonique',
        'Soul Douse': 'Assombrissement des âmes',
        'Soulweave': 'Entrelacement des âmes',
        'Sparking Fissure': 'Électrification planaire',
        'Telltale Tears': 'Larmes spectrales',
        'Turali Stone IV': 'Giga Terre turalienne',
      },
    },
    {
      'locale': 'ja',
      'missingTranslations': true,
      'replaceSync': {
        'Lindblum Zaghnal': 'リンドブルム・ザグナル',
        'Lunipyati': 'ルニピャティ',
        'Overseer Kanilokka': '所長カニロッカ',
        'Preserved Soul': '所員の亡魂',
      },
      'replaceText': {
        'Beastly Roar': 'ビーストロア',
        'Bloodburst': 'ブラッドバースト',
        'Boulder Dance': 'ダンシングロック',
        'Caber Toss': '放り投げ',
        'Cell Shock': '放雷',
        'Crater Carve': 'クレーターカーヴ',
        'Dark II': 'ダーラ',
        'Dark Souls': 'ダークソウル',
        'Electrical Overload': '広域放電',
        'Free Spirits': 'ソウルリリース',
        'Gore': '牙を刺す',
        'Jagged Edge': 'ロックスパイク',
        'Leaping Earth': 'ホッピングアース',
        'Leporine Loaf': 'ラビットローフ',
        'Lightning Bolt': '落雷',
        'Lightning Storm': '百雷',
        'Line Voltage': '線状放雷',
        'Lost Hope': '自失の叫び',
        'Necrohazard': 'ネクロハザード',
        'Phantom Flood': 'ファントムフラッド',
        'Raging Claw': 'レイジングクロウ',
        'Rock Blast': 'ロックブラスト',
        'Slabber': 'サライヴァスピット',
        'Sonic Howl': 'ソニックハウル',
        'Soul Douse': 'ソウルダウス',
        'Soulweave': 'ソウルウィーブ',
        'Sparking Fissure': '対地放雷',
        'Telltale Tears': 'ファントムティアー',
        'Turali Stone IV': 'トラルストンジャ',
      },
    },
    {
      'locale': 'cn',
      'replaceSync': {
        'Lindblum Zaghnal': '林德布鲁姆扎戈斧龙',
        'Lunipyati': '鲁尼皮亚提',
        'Overseer Kanilokka': '卡尼洛喀站长',
        'Preserved Soul': '站员的亡魂',
      },
      'replaceText': {
        '\\(arcs\\)': '(弧线)',
        '\\(big\\)': '(大)',
        '\\(multiple\\)': '(多重)',
        '\\(spiral\\)': '(螺旋)',
        '--adds 1 targetable--': '--小怪 1 可选中--',
        '--adds 2 targetable--': '--小怪 2 可选中--',
        '--adds 3 targetable--': '--小怪 3 可选中--',
        'Beastly Roar': '残虐咆哮',
        'Bloodburst': '血爆',
        'Boulder Dance': '舞动的岩石',
        'Caber Toss': '投掷',
        'Cell Shock': '放电',
        'Crater Carve': '刨坑',
        'Dark II': '昏暗',
        'Dark Souls': '黑暗之魂',
        'Electrical Overload': '广域奔雷',
        'Free Spirits': '灵魂释放',
        'Gore': '刺牙',
        'Jagged Edge': '岩石突击',
        'Leaping Earth': '跃动的大地',
        'Leporine Loaf': '稳如兔山',
        'Lost Hope': '恍惚的叫声',
        'Lightning Bolt': '落雷',
        'Lightning Storm': '百雷',
        'Line Voltage': '线状放电',
        'Necrohazard': '死灵危机',
        'Phantom Flood': '幽魂泛滥',
        'Raging Claw': '暴怒连爪',
        'Rock Blast': '岩石冲击',
        'Slabber': '唾液飞溅',
        'Sparking Fissure': '对地放电',
        'Sonic Howl': '音嚎',
        'Soul Douse': '失魂',
        'Soulweave': '灵魂飞掠',
        'Telltale Tears': '幽魂之泪',
        'Turali Stone IV': '图拉尔崩石',
      },
    },
    {
      'locale': 'tc',
      'replaceSync': {
        'Lindblum Zaghnal': '林德布魯姆扎古納魯',
        'Lunipyati': '魯尼皮亞提',
        'Overseer Kanilokka': '卡尼洛喀站長',
        'Preserved Soul': '站員的亡魂',
      },
      'replaceText': {
        '\\(arcs\\)': '(弧線)',
        '\\(big\\)': '(大)',
        '\\(multiple\\)': '(多重)',
        '\\(spiral\\)': '(螺旋)',
        '--adds 1 targetable--': '--小怪 1 可選中--',
        '--adds 2 targetable--': '--小怪 2 可選中--',
        '--adds 3 targetable--': '--小怪 3 可選中--',
        'Beastly Roar': '殘虐咆哮',
        'Bloodburst': '血爆',
        'Boulder Dance': '舞動的岩石',
        'Caber Toss': '投擲',
        'Cell Shock': '放電',
        'Crater Carve': '刨坑',
        'Dark II': '昏暗',
        'Dark Souls': '黑暗之魂',
        'Electrical Overload': '廣域放電',
        'Free Spirits': '靈魂釋放',
        'Gore': '刺牙',
        'Jagged Edge': '岩石突擊',
        'Leaping Earth': '躍動的大地',
        'Leporine Loaf': '穩如兔山',
        'Lost Hope': '恍惚的叫聲',
        'Lightning Bolt': '落雷',
        'Lightning Storm': '百雷',
        'Line Voltage': '線狀放電',
        'Necrohazard': '死靈危機',
        'Phantom Flood': '幽魂泛濫',
        'Raging Claw': '暴怒連爪',
        'Rock Blast': '岩石衝擊',
        'Slabber': '唾液飛濺',
        'Sparking Fissure': '對地放電',
        'Sonic Howl': '音嚎',
        'Soul Douse': '失魂',
        'Soulweave': '靈魂飛掠',
        'Telltale Tears': '幽魂之淚',
        'Turali Stone IV': '圖拉爾超巨岩',
      },
    },
    {
      'locale': 'ko',
      'replaceSync': {
        'Lindblum Zaghnal': '린드블룸 자그날',
        'Overseer Kanilokka': '소장 카닐로카',
        'Lunipyati': '루니퍄티',
      },
      'replaceText': {
        '\\(arcs\\)': '(원호)',
        '\\(big\\)': '(강력)',
        '\\(multiple\\)': '(다중)',
        '\\(spiral\\)': '(나선)',
        '--adds': '--쫄',
        '(?<!un)targetable--': '타겟 가능--',
        'Beastly Roar': '야수의 포효',
        'Bloodburst': '피의 범람',
        'Boulder Dance': '춤추는 바위',
        'Caber Toss': '집어던지기',
        'Cell Shock': '방뢰',
        'Crater Carve': '구덩이 파기',
        'Dark II': '다라',
        'Dark Souls': '암울한 영혼',
        'Electrical Overload': '광역 방전',
        'Free Spirits': '영혼 해방',
        'Gore': '엄니 찌르기',
        'Jagged Edge': '바위 꿰기',
        'Leaping Earth': '솟구치는 대지',
        'Leporine Loaf': '토끼 웅크리기',
        'Lost Hope': '망연자실한 외침',
        'Lightning Bolt': '낙뢰',
        'Lightning Storm': '백뢰',
        'Line Voltage': '직선형 방뢰',
        'Necrohazard': '죽음의 재해',
        'Phantom Flood': '혼령의 늪',
        'Raging Claw': '성난 발톱',
        'Rock Blast': '바위 폭풍',
        'Slabber': '침 흘리기',
        'Sparking Fissure': '지면 방뢰',
        'Sonic Howl': '음속 포효',
        'Soul Douse': '영혼 분리',
        'Soulweave': '영혼 돌진',
        'Telltale Tears': '혼령의 눈물',
        'Turali Stone IV': '투랄 스톤쟈',
      },
    },
  ],
};

export default triggerSet;
